/**
 * /operations/eos-dispute — EOS Dispute 4-agent keystone
 * (Pack 9 v2 UI). The most-visible 4-agent coordination in the
 * product. Surfaces the dispute workflow: Khidmat β-sentinel
 * refusing during active appeal; Mutaba'aa veto path; Rawatib
 * settlement modes; Tashghil orchestration.
 */


import { PageHeader } from "../components/ui";
import { AgentBadge } from "../components/ui/AgentBadge";

const FOUR_AGENT_FLOW = [
  {
    agent: "tashghil" as const,
    step: "1. Termination initiated",
    desc: "Tashghil drives the termination workflow; classifies the cause via the Pack 2 Article 80/81 enum.",
  },
  {
    agent: "mutabaa" as const,
    step: "2. Mutaba'aa enforces Article 80 5-condition checklist",
    desc: "Veto fires if absconded is invoked without all 5 conditions True. Disciplinary process audit required.",
  },
  {
    agent: "rawatib" as const,
    step: "3. Rawatib computes settlement",
    desc: "Per the cause sub-category — full EOS / partial / zero. Article 88 SLA enforced.",
  },
  {
    agent: "khidmat" as const,
    step: "4. Khidmat surfaces employee notifications",
    desc: "If employee files appeal: β-sentinel kicks in. Khidmat refuses to quote the settlement until ruling lands.",
  },
];

const DISPUTE_STATES = [
  { state: "none", desc: "No appeal filed; settlement runs normally." },
  { state: "appeal_filed", desc: "Employee filed within Article 222 window; dispute state engaged." },
  { state: "appeal_in_court", desc: "Court has accepted the appeal; awaiting ruling. β-sentinel active." },
  { state: "ruling_for_employer", desc: "Court ruled for employer; original settlement stands." },
  { state: "ruling_for_employee", desc: "Court ruled for employee; settlement amended via Mutaba'aa proposal." },
  { state: "mutual_withdrawal", desc: "Parties withdrew; settlement proceeds per original." },
  { state: "appeal_period_lapsed", desc: "12-month window passed; settlement locked." },
];

export function EosDisputePage() {
  return (
    <div className="space-y-6">
      <PageHeader
        title="EOS Dispute (4-agent keystone)"
        subtitle="The locked dispute workflow — the most-visible 4-agent coordination in Solvait-AIHR."
      />

      <section className="bg-surface-default border border-border-default rounded-lg p-4">
        <h3 className="text-base font-semibold mb-3">
          The 4-agent dance
        </h3>
        <div className="space-y-3">
          {FOUR_AGENT_FLOW.map((s) => (
            <div
              key={s.step}
              className="flex items-start gap-3 p-3 border border-border-subtle rounded"
            >
              <AgentBadge agent={s.agent} />
              <div>
                <div className="font-medium text-sm">{s.step}</div>
                <div className="text-xs text-text-secondary mt-1">
                  {s.desc}
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-surface-default border border-border-default rounded-lg p-4">
        <div className="flex items-center gap-2 mb-3">
          <AgentBadge agent="mutabaa" />
          <h3 className="text-base font-semibold">
            7-state dispute machine
          </h3>
        </div>
        <div className="space-y-2">
          {DISPUTE_STATES.map((s) => (
            <div
              key={s.state}
              className="grid grid-cols-[180px_1fr] gap-2 text-sm py-1"
            >
              <code className="text-xs bg-surface-muted px-1.5 py-0.5 rounded">
                {s.state}
              </code>
              <span className="text-text-secondary">{s.desc}</span>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-surface-default border border-border-default rounded-lg p-4">
        <div className="flex items-center gap-2 mb-3">
          <AgentBadge agent="khidmat" />
          <h3 className="text-base font-semibold">
            β-sentinel — Khidmat refuses to quote during dispute
          </h3>
        </div>
        <p className="text-sm text-text-secondary">
          When an employee in active dispute asks Khidmat about
          their settlement, the agent honours the β-sentinel
          (commandment 27 + AGENT_SPECS.md §4). Khidmat refuses to
          quote a settlement amount until the ruling lands, in EN +
          AR, with a bilingual escalation path to HR.
        </p>
      </section>
    </div>
  );
}
